import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, Clock } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { phone } from '@/content/site'

const contactInfo = [
  { icon: Phone, label: 'Phone', value: phone, href: `tel:${phone}` },
  { icon: Mail, label: 'Email', value: 'dpnelsons@gmail.com', href: 'mailto:dpnelsons@gmail.com' },
  { icon: MapPin, label: 'Location', value: 'California (virtual, worldwide)', href: null },
  { icon: Clock, label: 'Sessions', value: 'By appointment · Flexible time zones', href: null },
]

export default function ContactPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Get in Touch
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Let's Start a Conversation
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            No pitch. No pressure. Just a real conversation about where you are and what might help.
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16">

          {/* Left: Calendly CTA + contact info */}
          <div>
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-slate-900 rounded-3xl p-8 mb-8">
              <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Free Consultation</p>
              <h2 className="text-2xl font-bold text-white mb-3">Book a time with Douglas</h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                A free 30-minute call. We'll talk about where you are, what you've tried, and whether this work is a good fit.
              </p>
              <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full w-full justify-center hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
                Schedule Free Consultation
              </CalendlyBtn>
            </motion.div>

            <div className="space-y-4">
              {contactInfo.map(({ icon: Icon, label, value, href }, i) => (
                <motion.div key={label} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.07 }}
                  className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">{label}</p>
                    {href ? (
                      <a href={href} className="text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium transition-colors">
                        {value}
                      </a>
                    ) : (
                      <p className="text-slate-700 dark:text-slate-200 text-sm font-medium">{value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: Message form (future Google Apps Script integration) */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Send a Message</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Prefer email? Drop a note below and Douglas will be in touch.</p>
            <form className="space-y-4" onSubmit={e => { e.preventDefault(); alert('Form submission coming soon — use Calendly above!') }}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">First Name</label>
                  <input type="text" placeholder="Douglas"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm transition-shadow" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Last Name</label>
                  <input type="text" placeholder="Nelson"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm transition-shadow" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Email</label>
                <input type="email" placeholder="you@example.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm transition-shadow" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">What's on your mind?</label>
                <textarea rows={5} placeholder="Tell Douglas a little about what's bringing you to coaching..."
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm resize-none transition-shadow" />
              </div>
              <button type="submit"
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-medium text-sm transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-600/25">
                Send Message
              </button>
              <p className="text-xs text-slate-400 text-center">Form submission connects to Google Apps Script — coming soon.</p>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
